"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ConnectionFormProps, ConnectionFormValue } from "../ConnectionForm";
import { createSupportApi, type SupportApi, type SupportApiOptions } from "./api";

const EMPTY: ConnectionFormValue = { dashboardUrl: "", project: "", status: "not_configured" };

/**
 * Wires `<MemoProjectConnectionForm/>` to the host routes (`status`, `connect`, `poll`,
 * `disconnect`): `<MemoProjectConnectionForm {...useSupportConnection()} />`.
 */
export const useSupportConnection = (
  options: SupportApiOptions & { api?: SupportApi } = {},
): Pick<ConnectionFormProps, "value" | "onRequest" | "onDisconnect" | "onPoll" | "onDiscover"> & {
  reload: () => Promise<void>;
} => {
  const { api: given, basePath, fetch } = options;
  const api = useMemo(
    () => given ?? createSupportApi({ basePath, fetch }),
    [given, basePath, fetch],
  );
  const [value, setValue] = useState<ConnectionFormValue>(EMPTY);

  const reload = useCallback(async () => {
    try {
      setValue(await api.status());
    } catch {
      // Keep the last known state: a hiccup is not a disconnection.
    }
  }, [api]);
  useEffect(() => void reload(), [reload]);

  const onRequest = useCallback(
    async (draft: { dashboardUrl: string; project: string }) => {
      try {
        setValue(await api.connect(draft));
      } catch (error) {
        await reload();
        setValue((v) => ({ ...v, lastError: (error as Error).message }));
      }
    },
    [api, reload],
  );
  const onDisconnect = useCallback(async () => setValue(await api.disconnect()), [api]);
  // Poll, not status: pending → collect the token, active → notice a revocation.
  const onPoll = useCallback(async () => {
    try {
      setValue(await api.poll());
    } catch {
      // Same as reload: keep what we have.
    }
  }, [api]);

  // Discovery goes through the host too: the browser never talks to Flow directly.
  const onDiscover = useCallback(
    async (dashboardUrl: string) => {
      try {
        const found = await api.projects(dashboardUrl);
        return found.available ? found.projects : null;
      } catch {
        return null;
      }
    },
    [api],
  );

  return { value, onRequest, onDisconnect, onPoll, onDiscover, reload };
};
